import axios from 'axios'
import Swal from 'sweetalert2'
import Errors from './Errors'

export default class Form {

    /**
     * Create a new Form instance.
     *
     * @param {object} data
     */
    constructor(data) {
        // Save Original data
        this.originalData = {};
        this.fillAsOriginalData(data);

        // Set Properties
        this.errors = new Errors();
        this.success = false;
        this.loading = false;
        this.isDraft = false;
        this.onceUpdated = false;
        this.submittable = false;
        this.required = []
    }

    /**
     * Fetch all relevant data for the form.
     *
     * @returns {{}}
     */
    data() {
        let data = {};

        for (let property in this.originalData) {
            data[property] = this[property];
        }

        return data;
    }

    /**
     * Not null data.
     *
     * @returns {*}
     */
    notNullData(exceptions) {
        let data = JSON.parse(JSON.stringify(this.data()))
        return this.cleanObject(data, exceptions)
    }

    /**
     * Clean object.
     *
     * @param object
     * @returns {*}
     */
    cleanObject(object, exceptions) {
        Object
            .entries(object)
            .forEach(([k, v]) => {
                if (Array.isArray(exceptions) && exceptions.includes(k)) {
                    return false
                }
                if (v && typeof v === 'object') {
                    this.cleanObject(v);
                }
                if (v && typeof v === 'object' && !Object.keys(v).length || v === null || v === undefined) {
                    if (Array.isArray(object)) {
                        object.splice(k, 1);
                    } else {
                        delete object[k];
                    }
                }
            });
        return object;
    }

    /**
     * Reset the form fields.
     *
     */
    reset() {
        for (let field in this.originalData) {
            this[field] = '';
        }

        this.errors.clear();
        this.success = false;
    }

    /**
     * Send a POST request to the given URL.
     *
     * @param {string} url
     */
    post(url) {
        return this.submitUrl('post', url);
    }

    /**
     * Send a PUT request to the given URL.
     *
     * @param {string} url
     */
    put(url) {
        return this.submitUrl('put', url);
    }

    /**
     * Send a PATCH request to the given URL.
     *
     * @param {string} url
     */
    patch(url) {
        return this.submitUrl('patch', url);
    }

    /**
     * Send a DELETE request to the given URL.
     *
     * @param {string} url
     */
    delete(url) {
        return this.submitUrl('delete', url);
    }

    /**
     * Submit the form with URL.
     *
     * @param {string} requestType
     * @param {string} url
     */
    submitUrl(requestType, url) {
        this.loading = true;
        return new Promise((resolve, reject) => {
            axios[requestType](url, this.data())
                .then(response => {
                    this.onSuccess(response.data);
                    this.loading = false;
                    resolve(response.data);
                })
                .catch(error => {
                    this.onFail(error.response);
                    this.loading = false;
                    reject(this.errors);
                });
        });
    }

    /**
     * Submit the form with function.
     *
     * @param {function} funct
     * @returns {Promise<unknown>}
     */
    submit(funct) {
        this.loading = true;
        return new Promise((resolve, reject) => {
            funct()
                .then(response => {
                    this.onSuccess(response);
                    this.loading = false;
                    resolve(response);
                })
                .catch(error => {
                    this.onFail(error.response);
                    this.loading = false;
                    reject(this.errors);
                });
        });
    }

    /**
     * Transform data before submit.
     *
     * @param array
     * @param funct
     */
    transform(array, funct) {
        return array.map(x => funct(x));
    }

    /**
     * Handle a successful form submission.
     *
     * @param data
     */
    onSuccess(data) {
        this.success = data;
    }

    /**
     * Handle a failed form submission.
     *
     * @param errors
     */
    onFail(response) {
        // classic validation return
        if (response.status === 422) {
            this.errors.record(response.data)
        }
        // api return
        else if (response.data && response.data.status_code === 422) {
            this.errors.record(response.data.errors)
        }
        // convert message
        else if (response.data && response.data.errors) {
            this.errors.record({
                message: response.data.errors.message,
                errors: {}
            })
        }
        else if (response.data && response.data.message) {
            this.errors.record({
                message: response.data.message,
                errors: {}
            })
        }
        else {
            this.errors.record({
                message: typeof response === 'string' ? response : 'Error',
                errors: {}
            })
        }
    }

    /**
     * Copy translations into empty fields.
     *
     * @param languages
     * @param fieldsArray
     */
    fillEmptyTranslations(languages, fieldsArray) {
        const fieldsToTranslate = fieldsArray
        const isEmptyTextEditor = '<p></p>'

        fieldsToTranslate.forEach(key => {
            // Get value from non empty lang
            let fillValue = ''
            languages.map(lng => {
                if(this[key][lng] !== ''
                    && this[key][lng] !== null
                    && this[key][lng] !== isEmptyTextEditor)
                {
                    fillValue = this[key][lng]
                }
            })

            // Fill empty lang with selected value
            languages.map(lng => {
                if(this[key][lng] === '' || this[key][lng] === null || this[key][lng] === isEmptyTextEditor) {
                    this[key][lng] = fillValue
                }
            })
        });
    }

    /**
     * Alert popup before leaving draft form.
     *
     * @param {function} next
     */
    confirmBeforeLeaving(next) {
        Swal.fire({
            title: 'Attention',
            text: 'Vos changements ne sont pas enregistrés',
            icon: 'warning',
            showCancelButton: true,
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-success btn-lg mb-2',
                cancelButton: 'btn btn-light btn-lg'
            },
            confirmButtonText: 'Continuer (changer de page)',
            cancelButtonText: 'Non (rester sur la page)'
        }).then((result) => {
            if (result.value) {
                next()
            }
        })
    }

    /**
     * Fill original data.
     *
     * @param data
     */
    fillAsOriginalData(data) {
        this.originalData = Object.assign(this.originalData, JSON.parse(JSON.stringify(data)));

        for (let field in data) {
            this[field] = data[field];
        }

        this.checkSubmittable();
    }

    /**
     * Remove original data.
     *
     * @param field
     */
    removeOriginalData(field) {
        delete this.originalData[field];
        delete this[field];
    }

    /**
     * Form is editted
     *
     */
    keyUp() {
        this.isDraft = this.jsonStringify(this.data()) !== this.jsonStringify(this.originalData);
        this.onceUpdated = true;
        this.checkSubmittable();
    }

    /**
     * JSON Stringify.
     *
     * @param data
     */
    jsonStringify(data) {
        return JSON.stringify(data, (key, value) => {
            if (value === '' || value === undefined) return null
            return value
        })
    }

    /**
     * Check if form is submittable.
     *
     * @returns {boolean}
     */
    checkSubmittable() {
        this.submittable = this.isDraft && !this.loading && this.onceUpdated && !this.errors.any();
        return this.submittable;
    }

}
